/**
 * `activity` (§7.6) — the manager-only filtered history. Reached from Gate Detail's Access
 * management card; **there is no standalone Activity tab and no resident view** (D29). D25's
 * "two rows, never one" is the read contract this screen honors rather than enforces: a
 * failure and a later success are two rows here because the server never collapsed them.
 *
 * **Names resolve server-side, at request time** (§6) — `actorDisplayName` on each row is
 * already the manager's label for that gate or the subject's self-name; this screen never
 * calls `resolveDisplayName()` itself and never denormalizes a name into anything it stores.
 *
 * **`via: 'shabbat-automation'` renders "שעון שבת" as the actor, here, on the client** — P8
 * owns the row, this screen owns the string (§7 seam); the server never bakes it in.
 *
 * The day-grouped list is this PRD's; the export button and the Live toggle are **P15's** and
 * are not rendered here (out of scope, §7.6's own header makes them Stage 1.1). There is no
 * filter UI (2026-08-16, user request) — the transport/result query parameters
 * `getGateActivity()` still accepts stay unused from this screen.
 *
 * **`status-probe` rows are never rendered.** `transport/probe.ts`'s own comment settles it:
 * "these rows are not Activity" — a probe is a system observation of the gate, not something a
 * person did, and `implementation-notes.md` → P9 already requires the exclusion
 * ("P9 must exclude the action from the Activity read"). The server writes them for
 * `alarms.md`'s MQTT failure-rate metric and D68's rate-limit accounting; they are filtered here
 * rather than server-side because `query.ts` deliberately never drops a row by `action` (§5).
 *
 * **`ble-access` rows are never rendered either (D243), and the reason is the same one one rung
 * up.** The row is written by `GET /gates/{id}/ble-access`, which §9 requires be audit-logged and
 * which D243 does not touch — the row still exists, it is simply not Activity. **Almost every one
 * of them is a pre-fetch nobody performed:** `warmCredential` runs on a Gate Detail view and for
 * up to three tiles on Home, so a resident who merely *looked* at their gates filled a manager's
 * history with rows recording that they did. `credentials.ts` already states the principle —
 * *"a pre-fetch is not an event about the gate: nothing was tapped, nothing was sent"* — and this
 * is that sentence reaching the surface that was contradicting it.
 *
 * **Nothing is lost from the manager's view, because the row was never the evidence.** A local
 * open that *happened* arrives as its own `open` row through `POST /gates/{id}/receipts`; the
 * credential handover is the system fact underneath it. **The client cannot tell a warm from a
 * real open's fetch** — and must not be trusted to declare which it is — so the exclusion is by
 * `action`, whole, exactly as `status-probe`'s is.
 *
 * 🔸 **The stated cost: this filter runs after pagination.** A page whose rows are all excluded
 * renders thin behind a "load more", and warms are frequent enough to make that reachable.
 * Dropping them server-side would fix it and would breach §5's rule that `query.ts` never drops a
 * row by `action`; that trade was put to the owner and refused (D243).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { asIsoInstant, type GateId } from '@selogate/core'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { useDeviceCache } from '../../device-cache/context'
import { AppBar, Bidi, Chip, FrameOverlay, SecondaryButton } from '../../components'
import type { Key } from '../../i18n/catalogs'
import { useTranslation } from '../../i18n/I18nProvider'
import { formatDate, formatDateTime, formatTime } from '../../i18n/format'
import type { Locale } from '../../i18n/locales'
import { useDelayedIndicator } from '../../hooks/useDelayedIndicator'
import { useDismissOnBack, useNavigation } from '../../router'
import type { ActivityRow } from '../shared/api'
import { takeGateHandoff } from '../shared/gateHandoff'
import { fetchActivityPage, isVisible, type FetchedPage } from './fetchActivityPage'

/**
 * Three states, not a nullable page — the same reasoning `PeopleScreen`'s `RosterState` gives:
 * a `403` is a knowable, permanent refusal for this viewer (R1-7(c)), not a connectivity
 * problem, so it gets its own phase rather than a `failed` that invites a retry that cannot
 * succeed.
 */
type ActivityState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly rows: readonly ActivityRow[]; readonly nextCursor?: string }
  | { readonly phase: 'failed' }
  | { readonly phase: 'forbidden' }

type T = (key: Key<'activity'>, vars?: Readonly<Record<string, string | number>>) => string

const ACTION_KEYS: Readonly<Record<string, Key<'activity'>>> = {
  open: 'actionOpen',
  close: 'actionClose',
  stop: 'actionStop',
  latch: 'actionLatch',
  release: 'actionRelease',
  provision: 'actionProvision',
  'gate-edit': 'actionGateEdit',
  'grant-create': 'actionGrantCreate',
  'grant-edit': 'actionGrantEdit',
  'grant-revoke': 'actionGrantRevoke',
  'grant-expire': 'actionGrantExpire',
}

/** §5 — an unrecognized `action` renders as a neutral row, never a crash and never dropped. */
function actionLabel(t: T, row: ActivityRow): string {
  if (row.via === 'shabbat-automation') {
    return row.action === 'latch'
      ? t('actionLatch')
      : row.action === 'release'
        ? t('actionRelease')
        : t('automationActor')
  }
  const key = ACTION_KEYS[row.action]
  return key === undefined ? t('actionGeneric') : t(key)
}

function actorLabel(t: T, row: ActivityRow): string | undefined {
  if (row.via === 'shabbat-automation') return t('automationActor')
  return row.actorDisplayName
}

function groupKey(row: ActivityRow, locale: Locale): string {
  try {
    return formatDate(asIsoInstant(row.ts), locale)
  } catch {
    return row.ts
  }
}

export function ActivityScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t, locale } = useTranslation('activity')
  const { t: tCommon } = useTranslation('common')

  const cacheAnswered = deviceCache.origin !== undefined
  const cached = deviceCache.gates.find((g) => g.gateId === gateId)
  const gateName = cached?.alias ?? cached?.defaultName ?? ''

  const [state, setState] = useState<ActivityState>({ phase: 'loading' })
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedRow, setSelectedRow] = useState<ActivityRow | undefined>(undefined)

  useDismissOnBack('activity:row-detail', selectedRow !== undefined, () => setSelectedRow(undefined))

  /**
   * **R5 step 6 — the Access page may already have fetched this, and if it has, this screen opens
   * with no request of its own.**
   *
   * The prefetch runs `fetchActivityPage` with the same arguments, so what is taken here is the
   * same value this function would have computed, verdict included — a `forbidden` or `failed`
   * prefetch is deposited and honoured rather than re-attempted, exactly as a first-hand one is.
   * A miss (a deep link, a second visit, an offline Access page) falls through to the read, which
   * is the behaviour this screen has always had.
   */
  async function load(): Promise<void> {
    const handed = takeGateHandoff(gateId, 'activity')
    const page: FetchedPage = handed ?? (await fetchActivityPage(session.request, gateId, undefined))
    if (page.kind !== 'page') {
      setState({ phase: page.kind })
      return
    }
    setState({
      phase: 'ready',
      rows: page.rows,
      ...(page.nextCursor === undefined ? {} : { nextCursor: page.nextCursor }),
    })
  }

  useEffect(() => {
    setState({ phase: 'loading' })
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  async function loadMore(): Promise<void> {
    if (state.phase !== 'ready' || state.nextCursor === undefined || loadingMore) return
    setLoadingMore(true)
    try {
      /*
       * The walk runs here too, and for the sharper reason: this is the control a manager reaches
       * for *because* the page in front of them is short, so appending another all-hidden page is
       * a tap that visibly does nothing. A `forbidden` or `failed` outcome mid-list is swallowed
       * exactly as the single call's was — the rows already on screen are real and are not
       * replaced by an error state for a page that did not arrive.
       */
      const page = await fetchActivityPage(session.request, gateId, state.nextCursor)
      if (page.kind === 'page') {
        setState({
          phase: 'ready',
          rows: [...state.rows, ...page.rows],
          ...(page.nextCursor === undefined ? {} : { nextCursor: page.nextCursor }),
        })
      }
    } finally {
      setLoadingMore(false)
    }
  }

  function retry(): void {
    setState({ phase: 'loading' })
    void load()
  }

  const slowLoad = useDelayedIndicator(state.phase === 'loading')
  const rows = (state.phase === 'ready' ? state.rows : []).filter(isVisible)
  const groups = new Map<string, ActivityRow[]>()
  for (const row of rows) {
    const key = groupKey(row, locale)
    const existing = groups.get(key)
    if (existing === undefined) groups.set(key, [row])
    else existing.push(row)
  }

  return (
    <>
      <AppBar title={t('title', { name: gateName })} onBack={() => navigate.back()} />

      {state.phase === 'loading' ? (
        /* R5 step 7 (M7) — the Access page prefetches this page on entry, so most arrivals have
           nothing to wait for at all; what is left is the manager who taps through faster than the
           prefetch lands, and a spinner for that fraction of a second is a flash rather than a
           report. Delayed, never removed: unlike Gate Detail there is no snapshot to paint from,
           an audit page being exactly what a device-local allow-list must not hold. `null` and not
           an empty box — the app bar is already on screen and says where the reader is. */
        slowLoad ? (
          <div className="roster-notice">{t('loading')}</div>
        ) : null
      ) : state.phase === 'forbidden' ? (
        <div className="roster-notice">
          <p className="hint error">{t('forbidden')}</p>
        </div>
      ) : state.phase === 'failed' ? (
        <div className="roster-notice">
          <p className="hint error">{t('loadFailedTitle')}</p>
          <SecondaryButton onClick={retry}>{tCommon('retry')}</SecondaryButton>
        </div>
      ) : rows.length === 0 ? (
        <div className="roster-notice">
          <p className="hint">{t('emptyTitle')}</p>
          {/* D230 — an empty list is now ambiguous: nothing happened, or everything that did
              has passed the twelve-month window. "No activity yet" alone would state the first
              on evidence for neither. */}
          <p className="hint">{t('horizon')}</p>
          {/*
            **D243 — the empty state carries the control, because it is reachable with a cursor in
            hand.** This branch is tested *before* the list, and the list is where *load more* used
            to live exclusively, so a page the filter emptied rendered "no activity yet" over a log
            that had plenty and offered nothing to press. `AUTO_ADVANCE_ROUNDS` makes that rare; a
            bound makes it still possible, and a bound whose failure mode is a dead end is not a
            bound worth having. **`nextCursor` is the whole condition** — with no cursor the list
            really has ended, and a control there would promise a page that does not exist.
          */}
          {state.phase === 'ready' && state.nextCursor !== undefined ? (
            <div className="form-row">
              <SecondaryButton disabled={loadingMore} onClick={() => void loadMore()}>
                {loadingMore ? t('loadingMore') : t('loadMore')}
              </SecondaryButton>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {[...groups.entries()].map(([day, dayRows]) => (
            <div key={day}>
              <div className="day-group-label">{day}</div>
              {dayRows.map((row) => (
                <ActivityListRow
                  key={row.eventId}
                  row={row}
                  t={t}
                  locale={locale}
                  onOpen={() => setSelectedRow(row)}
                />
              ))}
            </div>
          ))}
          {state.phase === 'ready' && state.nextCursor !== undefined ? (
            <div className="form-row">
              <SecondaryButton disabled={loadingMore} onClick={() => void loadMore()}>
                {loadingMore ? t('loadingMore') : t('loadMore')}
              </SecondaryButton>
            </div>
          ) : (
            /* D230 — the end of the list is the end of the *record*, not of the gate's history.
               Before the retention window existed, "Load more" simply stopped appearing and the
               reader could reasonably read that as everything there ever was. */
            <div className="form-row">
              <p className="hint">{t('horizon')}</p>
            </div>
          )}
        </>
      )}

      {selectedRow !== undefined ? (
        <FrameOverlay>
          <div className="activity-detail-backdrop" onClick={() => setSelectedRow(undefined)}>
            <div
              className="activity-detail-sheet"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="section-title">{t('detailTitle')}</div>
              <DetailRow label={t('detailTimestamp')}>
                <Bidi>
                  {formatDateTime(asIsoInstant(selectedRow.ts), locale, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Bidi>
              </DetailRow>
              <DetailRow label={t('detailUser')}>{actorLabel(t, selectedRow) ?? t('unknownUser')}</DetailRow>
              <DetailRow label={t('detailGate')}>{gateName || t('noValue')}</DetailRow>
              <DetailRow label={t('detailTransport')}>
                {selectedRow.transport === 'cloud'
                  ? t('transportCloud')
                  : selectedRow.transport === 'ble'
                    ? t('transportBle')
                    : t('noValue')}
              </DetailRow>
              <DetailRow label={t('detailLatency')}>
                {selectedRow.latencyMs === undefined
                  ? t('noValue')
                  : t('latencyMs', { ms: selectedRow.latencyMs })}
              </DetailRow>
              <DetailRow label={t('detailDevice')}>
                <Bidi>{selectedRow.deviceId ?? t('noValue')}</Bidi>
              </DetailRow>
              {selectedRow.failureReason !== undefined ? (
                <DetailRow label={t('detailFailureReason')}>
                  <Bidi>{selectedRow.failureReason}</Bidi>
                </DetailRow>
              ) : null}
              {selectedRow.reconciliationId !== undefined ? (
                <p className="hint">{t('reconciledTag')}</p>
              ) : null}
            </div>
          </div>
        </FrameOverlay>
      ) : null}
    </>
  )
}

function DetailRow({
  label,
  children,
}: {
  readonly label: string
  readonly children: ReactNode
}): JSX.Element {
  return (
    <div className="row">
      <div className="l">{label}</div>
      <div className="r">{children}</div>
    </div>
  )
}

function ActivityListRow({
  row,
  t,
  locale,
  onOpen,
}: {
  readonly row: ActivityRow
  readonly t: T
  readonly locale: Locale
  onOpen(): void
}): JSX.Element {
  const time = (() => {
    try {
      return formatTime(asIsoInstant(row.ts), locale)
    } catch {
      return row.ts
    }
  })()
  const who = actorLabel(t, row)
  return (
    <button className="row" data-interactive="true" type="button" onClick={onOpen}>
      <div className="l">
        {row.result !== 'success' ? (
          <Chip variant="fail">{row.result === 'refused' ? t('resultRefused') : t('resultFailure')}</Chip>
        ) : null}{' '}
        {actionLabel(t, row)}
        {who !== undefined ? ` — ${who}` : ''}
        {row.reconciliationId !== undefined ? <span className="chip">{t('reconciledTag')}</span> : null}
      </div>
      <div className="r">
        <Bidi>{time}</Bidi>
        {row.transport !== undefined ? (
          <>
            {' · '}
            <Chip variant="transport">
              {row.transport === 'cloud' ? t('transportCloud') : t('transportBle')}
            </Chip>
          </>
        ) : null}
      </div>
    </button>
  )
}
